import { useEffect, useState } from "react";
import templates from "./templates";
import { useSessionContext } from "../../context/session.context";
import {
  getNotNegativeNumber,
  getObject,
  getString,
  isArrayEmpty,
  isStringEmpty,
} from "../../utils/handler_validations";
import { dynamicRateDB } from "../../services";
import { classVeryIconGoogle } from "../../components/common/v2/VeryIconGoogle/classVeryIconGoogle";
import { useVeryNotificationsContext } from "../../components/common/v2/VeryNotifications/useVeryNotifications";
import { simplify } from "@turf/turf";
import { updateAppStatus } from "../../services/appStatus";

const parseTimeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const regex = /^(\d{1,2}):(\d{2})\s?(AM|PM)$/i;
  const match = timeStr.trim().match(regex);
  if (!match) return 0;
  let hour = parseInt(match[1]);
  const minute = parseInt(match[2]);
  const period = match[3].toUpperCase();
  hour = hour % 12;
  if (period === "PM") hour += 12;
  return hour * 60 + minute;
};

const isRangeOverlapping = (start1, end1, start2, end2) => {
  const s1 = parseTimeToMinutes(start1);
  const e1 = parseTimeToMinutes(end1);
  const s2 = parseTimeToMinutes(start2);
  const e2 = parseTimeToMinutes(end2);

  const getSubRanges = (s, e) => {
    if (e < s) {
      return [{ start: s, end: 1440 }, { start: 0, end: e }];
    }
    if (s === e) {
      return [{ start: 0, end: 1440 }];
    }
    return [{ start: s, end: e }];
  };

  const ranges1 = getSubRanges(s1, e1);
  const ranges2 = getSubRanges(s2, e2);

  for (const r1 of ranges1) {
    for (const r2 of ranges2) {
      if (Math.max(r1.start, r2.start) < Math.min(r1.end, r2.end)) {
        return true;
      }
    }
  }
  return false;
};

export const useDynamicRate = () => {
  const [rowSelected, setRowSelected] = useState(-1);
  const [filterStatus, setFilterStatus] = useState({});
  const [data, setData] = useState(templates.getRowDynamicRate());
  const [selectedRateIds, setSelectedRateIds] = useState([]);
  const [generalBasePrice, setGeneralBasePrice] = useState("45");
  const [generalBaseKm, setGeneralBaseKm] = useState("3");
  const [generalExtraKmPrice, setGeneralExtraKmPrice] = useState("10");

  const [generalNightTariff1, setGeneralNightTariff1] = useState({
    startHour: "08:00 PM",
    endHour: "12:00 AM",
    increment: "10",
    active: true,
  });
  const [generalNightTariff2, setGeneralNightTariff2] = useState({
    startHour: "12:00 AM",
    endHour: "04:00 AM",
    increment: "20",
    active: true,
  });

  const [value, setValue] = useState({
    page: templates.getLoadError(true),
    contentPage: templates.getLoadError(false),
    list: [templates.getRowDynamicRate()],
    trips: {},
    logs: {},
    admins: {},
    users: {},
  });
  const [valueModal, setValueModal] = useState({
    show: false,
    stepSelected: 1,
    loading: false,
    editGeofenceOnly: false,
  });

  //hooks
  const session = useSessionContext();

  const notis = useVeryNotificationsContext();

  useEffect(() => {
    if (!isStringEmpty(session.value.jwt)) {
      getListInitial();
    }
  }, [session.value.jwt]);

  useEffect(() => {
    if (!value.page.loading && isStringEmpty(value.page.error.message)) {
      getListFiltered();
    }
  }, [filterStatus]);

  //obtener la data requerida para mostrar en el pageEdit
  const getRateSelected = () => {
    const data = {
      id: "",
      isActivate: false,
      name: "",
      ubicationName: "",
      ubicationCoordinates: {},
      initialRate: 0,
      isDynamic: false,
      pricePerKilometer: 0,
      kilometers: 0,
      startHour: "",
      endHour: "",
      priceOnDemand: 0,
      isDemand: false,
      startHour2: "",
      endHour2: "",
      priceOnDemand2: 0,
      isDemand2: false,
      color: "",
      priority: "",
      polygons: [],
    };
    if (isArrayEmpty(value.list) || rowSelected < 0) return data;
    const dataRow = value.list[rowSelected];
    data.id = dataRow.id;
    data.isActivate = dataRow.isActivate;
    data.name = dataRow.name;
    data.ubicationName = dataRow.ubicationName;
    data.ubicationCoordinates = dataRow.ubicationCoordinates;
    data.initialRate = dataRow.initialRate;
    data.isDynamic = dataRow.isDynamic;
    data.pricePerKilometer = dataRow.pricePerKilometer;
    data.kilometers = dataRow.kilometers;
    data.startHour = dataRow.startHour;
    data.endHour = dataRow.endHour;
    data.priceOnDemand = dataRow.priceOnDemand;
    data.isDemand = dataRow.isDemand;
    data.startHour2 = dataRow.startHour2;
    data.endHour2 = dataRow.endHour2;
    data.priceOnDemand2 = dataRow.priceOnDemand2;
    data.isDemand2 = dataRow.isDemand2;
    data.color = dataRow.color;
    data.priority = dataRow.priority;
    data.polygons = dataRow.polygons;

    return data;
  };

  const getEmptyMsgPage = () => {
    const emptyObj = { text: "", icon: { name: "", type: "" } };
    if (value.list === undefined) {
      emptyObj.text = "No se encontraron rutas dinamicas";
      emptyObj.icon.name = "content_paste";
      emptyObj.icon.type = classVeryIconGoogle.ICON_TYPE.SYMBOLS_OUTLINED;
    } else if (isArrayEmpty(value.list)) {
      emptyObj.text = "Ningúna ruta coincide con la búsqueda";
      emptyObj.icon.name = "content_paste_search";
      emptyObj.icon.type = classVeryIconGoogle.ICON_TYPE.SYMBOLS_OUTLINED;
    }
    return emptyObj;
  };

  /*===========================================
    HANDLE DYNAMIC RATE TABLE
  =============================================*/
  // Obtener la lista de rutas dinamicas formateada para ser manipulada por la tabla
  // Obtener la lista de cupones formateada para ser manipulada por la tabla
  const getFormattedList = (dynamicRatesList = [], initial = false) => {
    const list = isArrayEmpty(dynamicRatesList)
      ? initial
        ? undefined
        : []
      : dynamicRatesList;
    return list?.map((dynamicRate, index) => {
      const dataRow = templates.getRowDynamicRate();
      dataRow.rowId = index;
      dataRow.id = getString(dynamicRate.id);
      dataRow.isActivate = dynamicRate.isActivate == true || dynamicRate.isActivate === 1;
      dataRow.name = getString(dynamicRate.name);
      dataRow.ubicationName = getString(dynamicRate.ubicationName);
      dataRow.ubicationCoordinates = dynamicRate.ubicationCoordinates;
      dataRow.initialRate = getNotNegativeNumber(dynamicRate.initialRate);
      dataRow.isDynamic = dynamicRate.isDynamic == true || dynamicRate.isDynamic === 1;
      dataRow.pricePerKilometer = getNotNegativeNumber(
        dynamicRate.pricePerKilometer,
      );
      dataRow.kilometers = getNotNegativeNumber(dynamicRate.kilometers);
      dataRow.startHour = getString(dynamicRate.startHour);
      dataRow.endHour = getString(dynamicRate.endHour);
      dataRow.priceOnDemand = getNotNegativeNumber(dynamicRate.priceOnDemand);
      dataRow.isDemand = dynamicRate.isDemand == true || dynamicRate.isDemand === 1;
      dataRow.startHour2 = getString(dynamicRate.startHour2);
      dataRow.endHour2 = getString(dynamicRate.endHour2);
      dataRow.priceOnDemand2 = getNotNegativeNumber(dynamicRate.priceOnDemand2);
      dataRow.isDemand2 = dynamicRate.isDemand2 == true || dynamicRate.isDemand2 === 1;
      dataRow.color = getString(dynamicRate.color);
      dataRow.priority = getString(dynamicRate.priority);
      dataRow.polygons =
        dynamicRate.polygons === undefined ? [] : dynamicRate.polygons;
      dataRow.createdBy = getString(dynamicRate.createdBy);
      return dataRow;
    });
  };

  const getListFiltered = async () => {
    setValue((prev) => ({
      ...prev,
      contentPage: templates.getLoadError(true),
    }));
    setRowSelected(-1);
    console.log(session.value.jwt);
    const resp = await dynamicRateDB.getListDynamicRates(
      "",
      {},
      session.value.jwt,
    );
    console.log(resp);
    if (resp.error) {
      setValue((prev) => ({
        ...prev,
        contentPage: {
          loading: false,
          error: { message: resp.error.message, details: resp.error.details },
        },
      }));
      return;
    }
    // success
    setValue((prev) => ({
      ...prev,
      contentPage: templates.getLoadError(false),
      list: getFormattedList(resp.body.rates, true),
      trips: getObject(resp.body.trips),
      logs: getObject(resp.body.logs),
      admins: getObject(resp.body.admins),
      users: getObject(resp.body.users),
    }));
    setGeneralBasePrice(resp.body.generalBasePrice || "45");
    setGeneralBaseKm(resp.body.generalBaseKm || "3");
    setGeneralExtraKmPrice(resp.body.generalExtraKmPrice || "10");
  };

  const getListInitial = async () => {
    setValue((prev) => ({
      ...prev,
      page: templates.getLoadError(true),
    }));
    const resp = await dynamicRateDB.getListDynamicRates(
      "",
      {},
      session.value.jwt,
    );
    if (resp.error) {
      setValue((prev) => ({
        ...prev,
        page: {
          loading: false,
          error: { message: resp.error.message, details: resp.error.details },
        },
      }));
      return;
    }
    setValue((prev) => ({
      ...prev,
      page: templates.getLoadError(false),
      list: getFormattedList(resp.body.rates, false),
      trips: getObject(resp.body.trips),
      logs: getObject(resp.body.logs),
      admins: getObject(resp.body.admins),
      users: getObject(resp.body.users),
    }));
    setGeneralBasePrice(resp.body.generalBasePrice || "45");
    setGeneralBaseKm(resp.body.generalBaseKm || "3");
    setGeneralExtraKmPrice(resp.body.generalExtraKmPrice || "10");

    try {
      const tariffs = JSON.parse(resp.body.generalNightTariffs || "[]");
      if (Array.isArray(tariffs)) {
        if (tariffs[0]) {
          setGeneralNightTariff1({
            startHour: tariffs[0].startHour || "08:00 PM",
            endHour: tariffs[0].endHour || "12:00 AM",
            increment: tariffs[0].increment?.toString() || "10",
            active: tariffs[0].active !== false,
          });
        } else {
          setGeneralNightTariff1({ startHour: "08:00 PM", endHour: "12:00 AM", increment: "10", active: false });
        }
        if (tariffs[1]) {
          setGeneralNightTariff2({
            startHour: tariffs[1].startHour || "12:00 AM",
            endHour: tariffs[1].endHour || "04:00 AM",
            increment: tariffs[1].increment?.toString() || "20",
            active: tariffs[1].active !== false,
          });
        } else {
          setGeneralNightTariff2({ startHour: "12:00 AM", endHour: "04:00 AM", increment: "20", active: false });
        }
      }
    } catch (e) {
      console.log("Error parsing generalNightTariffs in hook:", e);
    }
  };

  const updateData = (newData = templates.getRowDynamicRate()) => {
    console.log(newData);
    setData((prev) => ({
      ...prev,
      ...newData,
    }));
  };

  const saveGeneralRateConfig = async () => {
    if (generalNightTariff1.active && generalNightTariff2.active) {
      if (isRangeOverlapping(generalNightTariff1.startHour, generalNightTariff1.endHour, generalNightTariff2.startHour, generalNightTariff2.endHour)) {
        notis.createNotiError("Conflicto de Horarios", "Los rangos de hora para la Tarifa Nocturna 1 y Tarifa Nocturna 2 se cruzan o traslapan.");
        return;
      }
    }

    setValue((prev) => ({ ...prev, page: templates.getLoadError(true) }));
    
    const tariffsToSend = [
      {
        name: "Tarifa 1",
        startHour: generalNightTariff1.startHour,
        endHour: generalNightTariff1.endHour,
        increment: parseFloat(generalNightTariff1.increment) || 0,
        active: generalNightTariff1.active === true,
      },
      {
        name: "Tarifa 2",
        startHour: generalNightTariff2.startHour,
        endHour: generalNightTariff2.endHour,
        increment: parseFloat(generalNightTariff2.increment) || 0,
        active: generalNightTariff2.active === true,
      }
    ];

    const resp = await updateAppStatus(
      {
        GENERAL_BASE_PRICE: generalBasePrice,
        GENERAL_BASE_KM: generalBaseKm,
        GENERAL_EXTRA_KM_PRICE: generalExtraKmPrice,
        GENERAL_NIGHT_TARIFFS: JSON.stringify(tariffsToSend),
      },
      session.value.jwt
    );
    if (resp.error) {
      setValue((prev) => ({ ...prev, page: templates.getLoadError(false) }));
      notis.createNotiError("Error al guardar tarifa general", resp.error.message);
      return;
    }
    setValue((prev) => ({ ...prev, page: templates.getLoadError(false) }));
    notis.createNotiSuccess("Tarifa general guardada", "La configuración general del sistema se guardó correctamente.");
  };

  const showContainerModeEdit = (
    initialData = templates.getRowDynamicRate(),
  ) => {
    updateData(initialData);
  };

  const onChangeRowSelected = (newRowSelected = -1) => {
    console.log(newRowSelected);
    setRowSelected(newRowSelected);
  };

  const onClickBtnSave = async () => {
    const dataToSend = {
      ...data,
      isDemand: false,
      isDemand2: false,
    };

    const respEdit = await dynamicRateDB.updateDynamicRate(
      dataToSend,
      session.value.jwt,
    );
    if (respEdit.error) {
      notis.createNotiError(respEdit.error.message, respEdit.error.details);

      return;
    }
    notis.createNotiSuccess(
      "Ruta dinámica editada correctamente",
      respEdit.messageSuccess,
    );
    getListFiltered();
  };

  const onClickBtnAdd = async () => {
    setValueModal((prev) => ({
      ...prev,
      loading: true,
    }));
    const dataToSend = {
      ...data,
      ubicationName:
        !data.ubicationName || data.ubicationName.trim() === ""
          ? "Sin especificar"
          : data.ubicationName,
      kilometers: Math.max(1, parseInt(data.kilometers) || 1),
      initialRate: parseFloat(data.initialRate) || 0,
      pricePerKilometer: parseFloat(data.pricePerKilometer) || 0,
      priceOnDemand: 0,
      startHour: "12:00 AM",
      endHour: "12:00 AM",
      isDemand: false,
      priceOnDemand2: 0,
      startHour2: "12:00 AM",
      endHour2: "12:00 AM",
      isDemand2: false,
    };
    const respAdd = await dynamicRateDB.createDynamicRate(
      dataToSend,
      session.value.jwt,
    );
    if (respAdd.error) {
      notis.createNotiError(respAdd.error.message, respAdd.error.details);
      setValueModal((prev) => ({
        ...prev,
        loading: false,
      }));
      return;
    }
    notis.createNotiSuccess(
      "Ruta dinámica agregada correctamente",
      respAdd.messageSuccess,
    );

    setValueModal(() => ({
      show: false,
      stepSelected: 1,
      loading: false,
    }));
    setData(templates.getRowDynamicRate());
    getListFiltered();
  };

  const onClickBtnActivate = async (updatedRow) => {
    const respActivate = await dynamicRateDB.updateDynamicRate(
      updatedRow,
      session.value.jwt,
    );
    if (respActivate.error) {
      notis.createNotiError(
        respActivate.error.message,
        respActivate.error.details,
      );

      return;
    }
    notis.createNotiSuccess(
      `Ruta dinamica ${
        updatedRow.isActivate ? "activada" : "desactivada"
      } correctamente`,
      respActivate.messageSuccess,
    );

    setValue((prev) => ({
      ...prev,
      list: prev.list.map((item) =>
        item.id === updatedRow.id
          ? {
              ...item,
              isActivate: updatedRow.isActivate,
            }
          : item,
      ),
    }));
  };

  const onClickBtnDelete = async () => {
    const respDelete = await dynamicRateDB.deleteRate(
      selectedRateIds,
      session.value.jwt,
    );
    if (respDelete.error) {
      notis.createNotiError(respDelete.error.message, respDelete.error.details);

      return;
    }
    notis.createNotiSuccess(
      `Ruta dinamica eliminada correctamente`,
      respDelete.messageSuccess,
    );
    setValue((prev) => ({
      ...prev,
      list: prev.list.filter((row) => !selectedRateIds.includes(row.id)),
    }));
    setSelectedRateIds([]);
  };

  const getPaths = async (name = "") => {
    const respPaths = await dynamicRateDB.getLocalityPaths(name);
    if (!Array.isArray(respPaths)) return [];

    // Filtramos los lugares útiles
    const filteredPlaces = respPaths.filter((place) => {
      const isBoundary = place.class === "boundary";
      const isAdmin = place.type === "administrative";
      const isPolygon =
        place.geojson?.type === "Polygon" ||
        place.geojson?.type === "MultiPolygon";
      const isLevelWeWant = ["county", "state", "country"].includes(
        place.addresstype,
      ); // puedes modificar aquí si solo quieres 'state' por ejemplo

      return isBoundary && isAdmin && isPolygon && isLevelWeWant;
    });

    if (filteredPlaces.length === 0) {
      console.warn("No se encontró ningún lugar con polígono válido.");
      return [];
    }

    // Tomamos el primero (puedes ordenarlo si quieres el más importante o específico)
    const place = filteredPlaces[0];
    const geojson = place.geojson;

    let coordinates = [];

    if (geojson.type === "Polygon") {
      coordinates = geojson.coordinates[0];
    } else if (geojson.type === "MultiPolygon") {
      let maxArea = 0;
      let largestPolygon = geojson.coordinates[0];

      for (const poly of geojson.coordinates) {
        const area = Math.abs(polygonArea(poly[0]));
        if (area > maxArea) {
          maxArea = area;
          largestPolygon = poly;
        }
      }

      coordinates = largestPolygon[0];
    }

    const formattedCoordinates = coordinates.map((coord) => ({
      lat: coord[1],
      lng: coord[0],
    }));

    // Simplificamos los paths
    const simplifiedPaths = simplificarPaths(formattedCoordinates, 0.001);
    // Simplificamos los paths
    console.log("Paths simplificados:", simplifiedPaths);

    return simplifiedPaths;
  };
  function simplificarPaths(googlePaths, tolerancia = 1.0) {
    // Convertir a [lng, lat] como espera turf
    const coordinates = googlePaths.map((p) => [p.lng, p.lat]);

    // Asegurar que el polígono se cierre
    if (
      coordinates.length > 0 &&
      (coordinates[0][0] !== coordinates.at(-1)[0] ||
        coordinates[0][1] !== coordinates.at(-1)[1])
    ) {
      coordinates.push(coordinates[0]); // cerrar el polígono
    }

    // Armar GeoJSON válido
    const polygon = {
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [coordinates],
      },
    };

    // Simplificar
    const simplified = simplify(polygon, {
      tolerance: tolerancia,
      highQuality: false,
    });

    // Convertir de regreso a formato Google Maps: { lat, lng }
    return simplified.geometry.coordinates[0].map(([lng, lat]) => ({
      lat,
      lng,
    }));
  }

  // Función para calcular área aproximada (shoelace)
  const polygonArea = (coords) => {
    let area = 0;
    for (let i = 0; i < coords.length; i++) {
      const [x1, y1] = coords[i];
      const [x2, y2] = coords[(i + 1) % coords.length];
      area += x1 * y2 - x2 * y1;
    }
    return area / 2;
  };

  const showModalAdd = () => {
    updateData(templates.getRowDynamicRate());
    setTimeout(() => {
      setValueModal({
        show: true,
        stepSelected: 1,
        loading: false,
        editGeofenceOnly: false,
      });
    }, 300);
  };

  const showModalEditGeofence = () => {
    setValueModal({
      show: true,
      stepSelected: 2.5,
      loading: false,
      editGeofenceOnly: true,
    });
  };

  const hideModal = () => {
    setValueModal((prev) => ({
      ...prev,
      show: false,
      loading: false,
      editGeofenceOnly: false,
    }));
  };

  const setStepSelected = (stepSelected = 1) => {
    setValueModal((prev) => ({
      ...prev,
      stepSelected,
    }));
  };

  return {
    value,
    valueModal,
    data,
    rowSelected,
    selectedRateIds,
    getListInitial,
    getListFiltered,
    getEmptyMsgPage,
    getRateSelected,

    updateData,
    showContainerModeEdit,
    onChangeRowSelected,
    onClickBtnSave,
    onClickBtnAdd,
    onClickBtnActivate,
    onClickBtnDelete,

    getPaths,

    showModalAdd,
    showModalEditGeofence,
    hideModal,

    setStepSelected,
    setSelectedRateIds,

    generalBasePrice,
    setGeneralBasePrice,
    generalBaseKm,
    setGeneralBaseKm,
    generalExtraKmPrice,
    setGeneralExtraKmPrice,
    generalNightTariff1,
    setGeneralNightTariff1,
    generalNightTariff2,
    setGeneralNightTariff2,
    saveGeneralRateConfig,
  };
};
